import { ReactNode, useEffect, useRef } from "react";
import { Dialog } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { useNavigate } from "react-router-dom";
import { publicRoute } from "routing";
import { LandingScreen } from "./landingScreen";

const nonComplianceSections = [
  {
    title: "Thématique 1 : Images (1)",
    items: [
      "Critère 1.3 : Page 01. Pour chaque image porteuse d'information ayant une alternative textuelle, cette alternative est pertinente (hors cas particuliers).",
    ],
  },
  {
    title: "Thématique 2 : Cadres (0)",
    items: [],
  },
  {
    title: "Thématique 3 : Couleurs (5)",
    items: [
      "Critère 3.1 : Pages 02, 03. Dans chaque page web, l'information ne doit pas être donnée uniquement par la couleur.",
      "Critère 3.2 : Pages 01, 02, 03. Dans chaque page web, le contraste entre la couleur du texte et la couleur de son arrière-plan est suffisamment élevé (hors cas particuliers).",
    ],
  },
  {
    title: "Thématique 4 : Multimédia (0)",
    items: [],
  },
  {
    title: "Thématique 5 : Tableaux (0)",
    items: [],
  },
  {
    title: "Thématique 6 : Liens (0)",
    items: [],
  },
  {
    title: "Thématique 7 : Scripts (5)",
    items: [
      "Critère 7.1 : Pages 01, 02, 03. Chaque script est, si nécessaire, compatible avec les technologies d’assistance.",
      "Critère 7.3 : Pages 01, 02. Chaque script est contrôlable par le clavier et par tout dispositif de pointage (hors cas particuliers).",
    ],
  },
  {
    title: "Thématique 8 : Éléments obligatoires (6)",
    items: [
      "Critère 8.4 : Pages 01, 02, 03. Pour chaque page web ayant une langue par défaut, le code de langue est pertinent.",
      "Critère 8.6 : Pages 01, 02, 03. Pour chaque page web ayant un titre de page, ce titre est pertinent.",
    ],
  },
  {
    title: "Thématique 9 : Structuration de l’information (6)",
    items: [
      "Critère 9.1 : Pages 01, 02, 03. Dans chaque page web, l’information est structurée par l’utilisation appropriée de titres.",
      "Critère 9.2 : Pages 01, 02, 03. Dans chaque page web, la structure du document est cohérente (hors cas particuliers).",
    ],
  },
  {
    title: "Thématique 10 : Présentation de l’information (13)",
    items: [
      "Critère 10.4 : Pages 01, 02, 03. Dans chaque page web, le texte reste lisible lorsque la taille des caractères est augmentée jusqu’à 200 %, au moins (hors cas particuliers).",
      "Critère 10.5 : Pages 01, 02, 03. Dans chaque page web, les déclarations CSS de couleurs de fond d’élément et de police sont correctement utilisées.",
      "Critère 10.7 : Page 01. Dans chaque page web, pour chaque élément recevant le focus, la prise de focus est visible.",
      "Critère 10.9 : Pages 01, 02, 03. Dans chaque page web, l’information ne doit pas être donnée uniquement par la forme, la taille ou la position.",
      "Critère 10.14 : Pages 01, 02, 03. Dans chaque page web, les contenus additionnels apparaissant via les styles CSS uniquement peuvent être rendus visibles au clavier et par tout dispositif de pointage.",
    ],
  },
  {
    title: "Thématique 11 : Formulaires (8)",
    items: [
      "Critère 11.1 : Pages 02, 03. Chaque champ de formulaire a une étiquette.",
      "Critère 11.5 : Pages 02, 03. Dans chaque formulaire, les champs de même nature sont regroupés, si nécessaire.",
      "Critère 11.6 : Pages 02, 03. Dans chaque formulaire, chaque regroupement de champs de même nature a une légende.",
      "Critère 11.8 : Page 02. Dans chaque formulaire, les items de même nature d’une liste de choix sont regroupés de manière pertinente.",
      "Critère 11.9 : Page 03. Dans chaque formulaire, l’intitulé de chaque bouton est pertinent (hors cas particuliers).",
    ],
  },
  {
    title: "Thématique 12 : Navigation (4)",
    items: [
      "Critère 12.8 : Pages 01, 02. Dans chaque page web, l’ordre de tabulation est cohérent.",
      "Critère 12.11 : Pages 01, 02. Dans chaque page web, les contenus additionnels apparaissant au survol, à la prise de focus ou à l’activation d’un composant d’interface sont, si nécessaire, atteignables au clavier.",
    ],
  },
  {
    title: "Thématique 13 : Consultation (0)",
    items: [],
  },
];

const assistiveTechnologyList = [
  "Mozilla Firefox (à titre principal) : 145.0.2 (64 bits) et NVDA 2025.3.2",
  "Windows 11",
];

const auditTools = [
  "Axe Dev Tools",
  "Web Developer Toolbar",
  "HeadingsMap",
  "Inspecteur de composants",
  "Validateur HTML du W3C",
  "Wave",
];

const checkedPages = [
  "https://simulateur-assurance-climatique.credit-agricole.fr/",
  "https://simulateur-assurance-climatique.credit-agricole.fr/",
  "https://simulateur-assurance-climatique.credit-agricole.fr/",
];

const appealLinks = [
  {
    href: "https://formulaire.defenseurdesdroits.fr/",
    label: "Écrire un message au Défenseur des droits",
  },
  {
    href: "https://www.defenseurdesdroits.fr/saisir/delegues",
    label: "Contacter le délégué du Défenseur des droits dans votre région",
  },
];

const editorialTextStyle = {
  fontFamily: "Georgia, 'Times New Roman', serif",
};

const inlineLinkClassName =
  "font-semibold text-[#0b7a6b] underline decoration-[#0b7a6b] underline-offset-2 hover:text-[#075c51]";

export const AccessibilityStatementScreen = () => {
  const navigate = useNavigate();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const closeStatement = () => {
    navigate(publicRoute.home);
  };

  useEffect(() => {
    document.title =
      "Déclaration d'accessibilité | Simulateur assurance climatique";
  }, []);

  return (
    <main className="relative min-h-screen w-full bg-[#eef2f3]">
      <div
        aria-hidden="true"
        className="pointer-events-none min-h-screen select-none overflow-hidden opacity-25 blur-[1px]"
      >
        <LandingScreen />
      </div>

      <Dialog
        open
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        initialFocus={closeButtonRef}
        onClose={closeStatement}
      >
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <div className="flex min-h-screen items-start justify-center p-2 sm:p-4">
          <div className="relative flex max-h-[calc(100vh-16px)] w-full max-w-[1510px] flex-col overflow-hidden bg-white shadow-[0_20px_60px_rgba(0,0,0,0.26)] sm:max-h-[calc(100vh-32px)]">
            <div className="flex flex-none justify-end border-b border-slate-200 bg-white px-4 py-3 sm:px-6">
              <button
                ref={closeButtonRef}
                type="button"
                aria-label="Fermer la déclaration d'accessibilité et revenir à l'accueil"
                className="inline-flex items-center gap-2 rounded-md border border-slate-400 bg-white px-3 py-2 text-[1rem] font-semibold leading-none text-[#4f4f4f] transition hover:border-[#22272d] hover:text-[#22272d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0b7a6b] focus-visible:ring-offset-2"
                onClick={closeStatement}
              >
                <span>Fermer</span>
                <XIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-8 sm:px-10 sm:py-10 lg:px-16 lg:py-12">
              <article
                className="border-l-2 border-[#0b7a6b] pl-6 sm:pl-10 lg:pl-14"
                style={editorialTextStyle}
              >
                <header className="pt-8">
                  <Dialog.Title
                    as="h1"
                    className="m-0 text-center text-[1.75rem] font-bold uppercase tracking-tight text-[#22272d] sm:text-[2rem] lg:text-[2.5rem]"
                  >
                    Déclaration d'accessibilité
                  </Dialog.Title>
                  <Dialog.Description className="sr-only">
                    Fenêtre modale contenant la déclaration d'accessibilité du
                    simulateur assurance climatique. Utilisez le bouton Fermer
                    ou la touche Échap pour revenir à l'accueil.
                  </Dialog.Description>
                  <p className="mt-10 text-[1rem] leading-[1.55] text-[#22272d] sm:text-[1.125rem]">
                    <strong>PACIFICA</strong> s'engage à rendre son site
                    internet accessible conformément à l'article 47 de la loi n°
                    2005-102 du 11 février 2005 et du décret n° 2019-768 du 24
                    juillet 2019.
                  </p>
                  <p className="mt-8 text-[1rem] leading-[1.55] text-[#22272d] sm:text-[1.125rem]">
                    À cette fin, un schéma d'accessibilité numérique et son plan
                    d'action sont en cours d'élaboration.
                  </p>
                  <p className="mt-8 text-[1rem] leading-[1.55] text-[#22272d] sm:text-[1.125rem]">
                    Cette déclaration d'accessibilité s'applique au site web{" "}
                    <a
                      href="https://simulateur-assurance-climatique.credit-agricole.fr/"
                      target="_blank"
                      rel="noreferrer"
                      className={inlineLinkClassName}
                    >
                      https://simulateur-assurance-climatique.credit-agricole.fr/
                    </a>{" "}
                    et vise à présenter la politique d'accessibilité du site.
                  </p>
                </header>

                <PageSection title="État de conformité">
                  <p className="m-0 text-[1rem] leading-[1.6] text-[#22272d] sm:text-[1.125rem]">
                    Le site{" "}
                    <a
                      href="https://simulateur-assurance-climatique.credit-agricole.fr/"
                      target="_blank"
                      rel="noreferrer"
                      className={inlineLinkClassName}
                    >
                      https://simulateur-assurance-climatique.credit-agricole.fr/
                    </a>{" "}
                    est <strong>partiellement conforme</strong> avec le
                    référentiel général d'amélioration de l'accessibilité en
                    raison des non-conformités énumérées ci-dessous.
                  </p>
                </PageSection>

                <PageSection title="Résultats des tests">
                  <p className="m-0 text-[1rem] leading-[1.6] text-[#22272d] sm:text-[1.125rem]">
                    L'audit de conformité réalisé par la société Infostrates
                    révèle que :
                  </p>
                  <p className="m-0 mt-8 pl-4 text-[1.125rem] font-semibold leading-[1.6] text-[#22272d] sm:pl-12 sm:text-[1.25rem]">
                    50 % des critères RGAA sont respectés.
                  </p>
                </PageSection>

                <PageSection title="Contenus non accessibles">
                  <p className="m-0 text-[1rem] leading-[1.6] text-[#22272d] sm:text-[1.125rem]">
                    Les contenus listés ci-dessous ne sont pas accessibles pour
                    les raisons suivantes.
                  </p>
                  <p className="m-0 mt-10 text-[1.25rem] font-semibold leading-[1.35] text-[#22272d] sm:text-[1.5rem]">
                    Non-conformité :
                  </p>
                  <div className="mt-8 space-y-12">
                    {nonComplianceSections.map((section) => (
                      <section key={section.title}>
                        <h3 className="m-0 text-[1.375rem] font-semibold leading-[1.25] text-[#22272d] sm:text-[1.75rem]">
                          {section.title}
                        </h3>
                        {section.items.length > 0 && (
                          <div className="mt-6 space-y-7">
                            {section.items.map((item) => {
                              const { heading, description } =
                                splitCriterion(item);

                              return (
                                <div key={item} className="pl-6 sm:pl-12">
                                  <p className="m-0 text-[1.125rem] font-semibold leading-[1.45] text-[#22272d] sm:text-[1.375rem]">
                                    - {heading}
                                  </p>
                                  {description && (
                                    <p className="m-0 mt-1 text-[1rem] leading-[1.55] text-[#22272d] sm:text-[1.125rem]">
                                      {description}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </section>
                    ))}
                  </div>
                </PageSection>

                <PageSection title="Établissement de cette déclaration d'accessibilité">
                  <div className="space-y-10 text-[1rem] leading-[1.6] text-[#22272d] sm:text-[1.125rem]">
                    <div>
                      <h3 className="m-0 text-[1.1875rem] font-semibold leading-[1.35] text-[#22272d] sm:text-[1.5rem]">
                        Date d'établissement
                      </h3>
                      <p className="mt-3">
                        Cette déclaration a été établie le 18 décembre 2025.
                      </p>
                    </div>

                    <div>
                      <h3 className="m-0 text-[1.1875rem] font-semibold leading-[1.35] text-[#22272d] sm:text-[1.5rem]">
                        Technologies utilisées pour la réalisation du site web
                      </h3>
                      <p className="mt-3">
                        HTML5 / CSS3 / JavaScript / TypeScript et React
                      </p>
                    </div>

                    <div>
                      <h3 className="m-0 text-[1.1875rem] font-semibold leading-[1.35] text-[#22272d] sm:text-[1.5rem]">
                        Agents utilisateurs, technologies d'assistance et outils
                        utilisés pour vérifier l'accessibilité
                      </h3>
                      <p className="mt-3">
                        Les tests des pages web ont été effectués avec les
                        combinaisons de navigateurs web et lecteurs d'écran
                        suivants :
                      </p>
                      <ul className="mt-4 space-y-2 pl-5">
                        {assistiveTechnologyList.map((item) => (
                          <li key={item} className="pl-4">
                            <span className="-ml-4 inline-block pr-4">-</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-4">
                        Les outils suivants ont été utilisés lors de
                        l'évaluation :
                      </p>
                      <ul className="mt-4 space-y-2 pl-5">
                        {auditTools.map((tool) => (
                          <li key={tool} className="pl-4">
                            <span className="-ml-4 inline-block pr-4">-</span>
                            {tool}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="m-0 text-[1.1875rem] font-semibold leading-[1.35] text-[#22272d] sm:text-[1.5rem]">
                        Pages du site ayant fait l'objet de la vérification de
                        conformité
                      </h3>
                      <ol className="mt-4 space-y-2 pl-5">
                        {checkedPages.map((page, index) => (
                          <li key={`${page}-${index}`} className="pl-4">
                            <span className="-ml-4 inline-block pr-4">-</span>
                            <a
                              href={page}
                              target="_blank"
                              rel="noreferrer"
                              className={inlineLinkClassName}
                            >
                              {page}
                            </a>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </PageSection>

                <PageSection title="Retour d'information et contact">
                  <p className="m-0 text-[1rem] leading-[1.6] text-[#22272d] sm:text-[1.125rem]">
                    Si vous n'arrivez pas à accéder à un contenu ou à un
                    service, vous pouvez contacter le responsable du site
                    internet pour être orienté vers une alternative accessible
                    ou obtenir le contenu sous une autre forme.
                  </p>
                  <p className="mt-6 text-[1rem] leading-[1.6] text-[#22272d] sm:text-[1.125rem]">
                    Contacter le responsable du site :{" "}
                    <a
                      href="mailto:accessibilite@ca-assurances.fr"
                      className={inlineLinkClassName}
                    >
                      accessibilite@ca-assurances.fr
                    </a>
                  </p>
                </PageSection>

                <PageSection title="Voies de recours">
                  <p className="m-0 text-[1rem] leading-[1.6] text-[#22272d] sm:text-[1.125rem]">
                    Cette procédure est à utiliser dans le cas suivant : vous
                    avez signalé au responsable du site internet un défaut
                    d'accessibilité qui vous empêche d'accéder à un contenu ou à
                    un des services du portail et vous n'avez pas obtenu de
                    réponse satisfaisante.
                  </p>
                  <ul className="mt-6 space-y-4 pl-5 text-[1rem] leading-[1.6] text-[#22272d] sm:text-[1.125rem]">
                    {appealLinks.map((link) => (
                      <li key={link.href} className="pl-4">
                        <span className="-ml-4 inline-block pr-4">-</span>
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noreferrer"
                          className={inlineLinkClassName}
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                    <li className="pl-4">
                      <span className="-ml-4 inline-block pr-4">-</span>
                      Envoyer un courrier par la poste (gratuit, ne pas mettre
                      de timbre) :
                      <address className="mt-2 not-italic">
                        Défenseur des droits
                        <br />
                        Libre réponse 71120
                        <br />
                        75342 Paris CEDEX 07
                      </address>
                    </li>
                  </ul>
                </PageSection>
              </article>
            </div>
          </div>
        </div>
      </Dialog>
    </main>
  );
};

function PageSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="mt-16">
      <h2
        className="m-0 text-[1.625rem] font-semibold leading-[1.2] text-[#22272d] sm:text-[2.125rem]"
        style={editorialTextStyle}
      >
        {title}
      </h2>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function splitCriterion(item: string) {
  const [heading, ...descriptionParts] = item.split(". ");

  return {
    heading,
    description: descriptionParts.join(". "),
  };
}
